import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone, X, Sprout, Wallet, BookOpen, Leaf, CheckCircle2 } from "lucide-react";

const PLAY_IMAGES = [
  "/play01.webp",
  "/play02.webp",
  "/play03.webp",
  "/play04.webp",
  "/play05.webp",
  "/play06.webp",
  "/play07.webp"
];

export default function AppDownloadFloating() {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [isAnimatingToPill, setIsAnimatingToPill] = useState(false);
  const [pillVisible, setPillVisible] = useState(false);
  const [isPillPulsing, setIsPillPulsing] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    // Only show if not already shown in this browser session
    const hasBeenShown = sessionStorage.getItem("rupiya_app_promo_shown");
    
    if (!hasBeenShown) {
      // Set timer to show modal after 10 seconds (10000ms) for testing
      const timer = setTimeout(() => {
        setShowPromoModal(true);
        sessionStorage.setItem("rupiya_app_promo_shown", "true");
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      // If already shown in session, just make the floating pill visible immediately
      setPillVisible(true);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // initial check
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!showPromoModal) return;
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => (prev + 1) % PLAY_IMAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [showPromoModal]);

  const handleClosePromo = () => {
    // Start shrink-to-pill flight animation
    setIsAnimatingToPill(true);
    
    // After animation finishes (850ms), switch states
    setTimeout(() => {
      setShowPromoModal(false);
      setIsAnimatingToPill(false);
      setPillVisible(true);
      
      // Trigger landing pulse on the pill
      setIsPillPulsing(true);
      setTimeout(() => {
        setIsPillPulsing(false);
      }, 800);
    }, 850);
  };

  if (dismissed || location.pathname === "/product-passport/detail") return null;

  return (
    <>
      {/* CSS Keyframes for Entry, Flight-Shrink and Landing Animations */}
      <style>{`
        @keyframes zoomInUp {
          0% {
            opacity: 0;
            transform: translate(-50%, -35%) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes shrinkToBottomLeft {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
            left: 50%;
            top: 50%;
          }
          100% {
            transform: translate(0, 0) scale(0.12);
            opacity: 0.1;
            left: 24px; /* Matches left-6 */
            top: calc(100vh - 48px); /* Targets the floating pill position at bottom-6 */
          }
        }
        @keyframes pillLandingPulse {
          0% {
            transform: scale(1);
          }
          40% {
            transform: scale(1.25);
            background-color: var(--natural-green);
            box-shadow: 0 0 0 15px rgba(78, 115, 59, 0.4);
          }
          70% {
            transform: scale(0.93);
            background-color: var(--natural-green);
          }
          100% {
            transform: scale(1);
          }
        }
        .anim-modal-entry {
          animation: zoomInUp 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .anim-shrink-flight {
          animation: shrinkToBottomLeft 0.85s cubic-bezier(0.25, 1, 0.5, 1) forwards;
          transform-origin: center center;
        }
        .anim-pill-landing {
          animation: pillLandingPulse 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards !important;
        }
      `}</style>

      {/* Promo Modal Overlay */}
      {showPromoModal && (
        <div 
          className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-550 ${
            isAnimatingToPill ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
        >
          {/* Modal Container */}
          <div
            data-testid="app-promo-modal"
            className={`fixed w-[calc(100%-2.5rem)] max-w-2xl bg-white rounded-3xl shadow-2xl border border-neutral-100 flex flex-col md:flex-row items-stretch overflow-hidden left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 origin-center ${
              isAnimatingToPill ? "anim-shrink-flight" : "anim-modal-entry"
            }`}
          >
            {/* Close button */}
            {!isAnimatingToPill && (
              <button
                onClick={handleClosePromo}
                data-testid="close-promo-modal"
                className="absolute top-4 right-4 p-1.5 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-all z-30"
                title="Dismiss Promo"
              >
                <X size={18} />
              </button>
            )}

            {/* Left Column: App Screenshot Slider (Flush to Left, Top, and Bottom) */}
            <div className="hidden md:block flex-shrink-0 w-52 relative bg-white border-r border-neutral-100">
              {PLAY_IMAGES.map((src, idx) => (
                <img
                  key={src}
                  src={src}
                  alt={`App Screen ${idx + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ${
                    idx === activeImageIndex
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-95 pointer-events-none"
                  }`}
                />
              ))}
              {/* Slider Indicators (dots) */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-30 bg-black/40 px-2 py-1 rounded-full backdrop-blur-[2px]">
                {PLAY_IMAGES.map((_, idx) => (
                  <span
                    key={idx}
                    onClick={() => setActiveImageIndex(idx)}
                    className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${
                      idx === activeImageIndex ? "bg-white scale-110" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Right Column: Promotional Pitch & QR / Badges */}
            <div className="flex-1 flex flex-col text-center md:text-left justify-center w-full p-6 md:p-8">
              <span className="text-[10px] tracking-[0.2em] font-bold text-[var(--natural-green)] mb-1">
                rupiya.app
              </span>
              <h3 className="font-display text-xl md:text-3xl font-bold text-[var(--charcoal)] leading-tight mb-2">
                Smart Agriculture in your pocket
              </h3>
              <p className="text-xs md:text-sm text-neutral-500 font-normal leading-relaxed mb-4 max-w-sm mx-auto md:mx-0">
                Get real-time agronomy advice, test soil health, secure interest-free farm inputs, and unlock carbon credit wealth.
              </p>

              {/* QR and Download Grid */}
              <div className="flex flex-col md:flex-row gap-4 items-center md:items-start bg-neutral-50 p-4 rounded-2xl border border-neutral-100 w-full">
                {/* QR Code */}
                <div className="hidden md:flex p-1.5 bg-white rounded-xl border border-neutral-200 shrink-0 w-[106px] h-[106px] items-center justify-center">
                  <img
                    src="/website_qr_code.png"
                    alt="Scan to download"
                    className="w-full h-full object-contain"
                  />
                </div>
                {/* Store Links */}
                <div className="flex flex-col justify-between md:h-[106px] w-full md:w-auto py-0.5 items-center md:items-start">
                  <span className="text-[10px] text-neutral-500 font-medium hidden md:block leading-none">
                    Scan or download directly:
                  </span>
                  <div className="flex flex-row md:flex-col gap-3 justify-center w-full">
                    <a
                      href="https://play.google.com/store/apps/details?id=rupiya.app&pli=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block transition-transform hover:scale-105 shrink-0"
                    >
                      <img
                        src="/google_play_badge.svg"
                        alt="Get it on Google Play"
                        className="h-[38px] w-auto object-contain"
                      />
                    </a>
                    <a
                      href="https://apps.apple.com/in/app/rupiya-app/id1641878325"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block transition-transform hover:scale-105 shrink-0"
                    >
                      <img
                        src="/app_store_badge.svg"
                        alt="Download on the App Store"
                        className="h-[38px] w-auto object-contain"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Capsule/Trigger (Bottom Left) */}
      <div
        data-testid="app-download-floating"
        className={`fixed bottom-6 left-6 z-40 hidden lg:flex flex-col items-start transition-all duration-500 pointer-events-none ${
          pillVisible && scrolled
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4"
        }`}
      >
        {/* Expanded Details Card */}
        <div 
          className={`mb-3 w-72 bg-white/95 backdrop-blur-md p-5 rounded-2xl shadow-2xl border border-neutral-100 flex flex-col items-center text-center transition-all duration-300 origin-bottom-left ${
            isCardOpen ? "scale-100 opacity-100 pointer-events-auto" : "scale-90 opacity-0 pointer-events-none"
          }`}
          style={{
            boxShadow: "0 20px 40px -15px rgba(43, 66, 34, 0.15)"
          }}
          onMouseEnter={() => setIsCardOpen(true)}
          onMouseLeave={() => setIsCardOpen(false)}
        >
            <div className="absolute top-3 right-3">
              <button
                onClick={() => setDismissed(true)}
                data-testid="dismiss-floating-widget"
                className="p-1 rounded-full text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>

            <h4 className="font-display text-base font-semibold text-[var(--charcoal)] mb-1">
              rupiya.app
            </h4>
            <p className="text-xs text-neutral-500 font-normal leading-normal mb-4">
              Scan QR code or click to download
            </p>

            {/* QR Code Container */}
            <div className="relative p-2 bg-neutral-50 rounded-xl border border-neutral-100 mb-4 flex items-center justify-center">
              <img
                src="/app_qr_code.png"
                alt="rupiya.app Download QR"
                className="w-28 h-28 object-contain"
                loading="lazy"
              />
            </div>

            {/* Download Store Badges - Side by Side Raw Badges */}
            <div className="grid grid-cols-2 gap-2 w-full items-center justify-items-center">
              <a
                href="https://play.google.com/store/apps/details?id=rupiya.app&pli=1"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-play-store"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="/google_play_badge.svg"
                  alt="Get it on Google Play"
                  className="h-[34px] w-auto object-contain"
                />
              </a>
              <a
                href="https://apps.apple.com/in/app/rupiya-app/id1641878325"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="floating-app-store"
                className="inline-block transition-transform hover:scale-105"
              >
                <img
                  src="/app_store_badge.svg"
                  alt="Download on the App Store"
                  className="h-[34px] w-auto object-contain"
                />
              </a>
            </div>
          </div>

          {/* Floating Capsule/Button */}
          <button
            data-testid="app-download-trigger"
            className={`flex items-center gap-2.5 px-4 py-3 rounded-full text-white shadow-xl transition-all duration-300 border border-white/10 relative overflow-hidden bg-[var(--forest-green)] hover:bg-[var(--natural-green)] hover:-translate-y-1 pointer-events-auto ${
              isPillPulsing ? "anim-pill-landing" : ""
            }`}
            style={{
              boxShadow: "0 10px 25px -5px rgba(43, 66, 34, 0.3)"
            }}
            onMouseEnter={() => setIsCardOpen(true)}
            onMouseLeave={() => setIsCardOpen(false)}
          >
            <div className="relative flex items-center justify-center">
              {/* Pulsing indicator */}
              <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-[var(--harvest-gold)] animate-ping opacity-75 -top-1 -right-1" />
              {/* <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--harvest-gold)] -top-1 -right-1" /> */}
              <Smartphone size={18} className="text-[var(--warm-white)]" />
            </div>
            <span className="text-sm font-semibold tracking-wide pr-1">
              Get rupiya.app
            </span>
          </button>
        </div>
      </>
  );
}
